import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Menu,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Archive as ArchiveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getAllCountries, AdminCountryDTO, getBoards, createBoard, updateBoard, deleteBoard, AdminBoardDTO } from '../services/adminLocationService';
import {
  getCoreSubjects,
  getCurriculumSubjects,
  getCurriculumSubjectById,
  createCurriculumSubject,
  updateCurriculumSubject,
  deleteCurriculumSubject,
  getCurriculumHierarchyFromBoardService,
  ensureScottishSubjectLink,
  scottishCurriculumManagementApi,
  getSubjectAdmins,
  assignAdminsToSubject,
  getCoreSubjectAdmins,
} from '../services/adminCourseService';
import { getAllSystemAdmins, SystemAdminDTO } from '../services/systemAdminService';
import { config } from '../config/environment';

interface Curriculum {
  id: number;
  countryId: number;
  countryName?: string;
  boardId: number;
  boardName?: string;
  curriculumSubjectId: number;
  subjectName?: string;
  version?: string;
  level?: string;
  description?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  scottishLevel?: string;
  scottishCurriculumId?: number;
}

interface CurriculumStructure {
  curriculum: Curriculum;
  areas: CurriculumArea[];
}

interface CurriculumArea {
  id: number;
  name: string;
  description?: string;
  displayOrder?: number;
  defaultVisibleToStudents: boolean;
  groups?: CurriculumGroup[];
}

interface CurriculumGroup {
  id: number;
  name: string;
  description?: string;
  displayOrder?: number;
  defaultVisibleToStudents: boolean;
  experienceOutcomes?: ExperienceOutcome[];
}

interface ExperienceOutcome {
  id: number;
  code: string;
  description: string;
  level?: string;
  displayOrder?: number;
  defaultVisibleToStudents: boolean;
}

const CurriculumManagement: React.FC = () => {
  const navigate = useNavigate();
  const { curriculumId } = useParams<{ curriculumId: string }>();
  const location = useLocation();
  const isStructureView = location.pathname.includes('/structure');
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCurriculum, setSelectedCurriculum] = useState<Curriculum | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCurriculum, setMenuCurriculum] = useState<Curriculum | null>(null);

  const [tabValue, setTabValue] = useState(0); // 0 = Boards, 1 = Curriculum Subjects, 2 = Course Curricula
  const [boardList, setBoardList] = useState<AdminBoardDTO[]>([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const [boardDeleteDialogOpen, setBoardDeleteDialogOpen] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<AdminBoardDTO | null>(null);
  const [menuBoard, setMenuBoard] = useState<AdminBoardDTO | null>(null);
  const [formBoardName, setFormBoardName] = useState('');
  const [formBoardCode, setFormBoardCode] = useState('');
  const [formBoardCountryId, setFormBoardCountryId] = useState<number | ''>('');
  const [formBoardType, setFormBoardType] = useState('SCOTTISH');
  const [formBoardLevel, setFormBoardLevel] = useState('NATIONAL');
  const [anchorElBoard, setAnchorElBoard] = useState<null | HTMLElement>(null);

  // Curriculum Subjects tab: board type drives which list/form is shown (Core vs Scottish)
  const scottishBaseUrl = config.scottishCurriculumApiUrl || 'http://localhost:8082';
  // Core subjects for dropdown when adding a Scottish curriculum subject (from Subjects & Topics)
  const [coreSubjectsForLink, setCoreSubjectsForLink] = useState<Array<{ id: number; code: string; name: string; description?: string }>>([]);
  interface CurrSubjectRow {
    coreId: number;
    code: string;
    name: string;
    description?: string;
    coreSubjectId?: number;
    boardId?: number;
    /** Board-specific service record id (e.g. scottish_curriculum_subjects.id). */
    boardLinkId?: number;
    boardName?: string;
  }
  const [curriculumSubjectsList, setCurriculumSubjectsList] = useState<CurrSubjectRow[]>([]);
  const [curriculumSubjectsLoading, setCurriculumSubjectsLoading] = useState(false);
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [subjectDeleteDialogOpen, setSubjectDeleteDialogOpen] = useState(false);
  const [selectedCurriculumSubject, setSelectedCurriculumSubject] = useState<CurrSubjectRow | null>(null);
  const [menuCurriculumSubject, setMenuCurriculumSubject] = useState<CurrSubjectRow | null>(null);
  /** Curriculum-subject admins keyed by curriculum_subject_id (CurrSubjectRow.coreId). */
  const [curriculumSubjectAdmins, setCurriculumSubjectAdmins] = useState<{ [csId: number]: SystemAdminDTO[] }>({});
  /** Core-subject admins keyed by core_subject_id; shown read-only as "contact for missing topics". */
  const [coreSubjectAdminsByCoreId, setCoreSubjectAdminsByCoreId] = useState<{ [coreSubjectId: number]: SystemAdminDTO[] }>({});
  const [allSystemAdmins, setAllSystemAdmins] = useState<SystemAdminDTO[]>([]);
  const [csAssignAdminsDialogOpen, setCsAssignAdminsDialogOpen] = useState(false);
  const [csAssignAdminsRow, setCsAssignAdminsRow] = useState<CurrSubjectRow | null>(null);
  const [csAssignSelectedAdminIds, setCsAssignSelectedAdminIds] = useState<number[]>([]);
  const [csAssignSaving, setCsAssignSaving] = useState(false);
  const [formSubjectName, setFormSubjectName] = useState('');
  const [formSubjectCode, setFormSubjectCode] = useState('');
  const [formSubjectDescription, setFormSubjectDescription] = useState('');
  const [formSubjectCoreId, setFormSubjectCoreId] = useState<number | ''>('');
  const [formSubjectBoardId, setFormSubjectBoardId] = useState<number | ''>('');
  const [anchorElSubject, setAnchorElSubject] = useState<null | HTMLElement>(null);

  // Form state for Course Curricula
  const [formCountryId, setFormCountryId] = useState<number | ''>('');
  const [formCurrBoardId, setFormCurrBoardId] = useState<number | ''>('');
  const [formCurrSubjectId, setFormCurrSubjectId] = useState<number | ''>('');
  const [formVersion, setFormVersion] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formScottishLevel, setFormScottishLevel] = useState('');

  // Available subjects from API (curriculum subjects filtered by board for the curricula form)
  const [subjects, setSubjects] = useState<{ id: number; name: string; code: string; description?: string }[]>([]);
  const [filteredCurrSubjects, setFilteredCurrSubjects] = useState<Array<{ id: number; name: string; code: string }>>([]);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const data = await getCurriculumSubjects();
        setSubjects(data);
      } catch (e) {
        console.error("Failed to load subjects", e);
      }
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    const fetchCoreSubjectsForLink = async () => {
      try {
        const data = await getCoreSubjects();
        setCoreSubjectsForLink(data);
      } catch (e) {
        console.error("Failed to load core subjects for link", e);
      }
    };
    if (tabValue === 1) fetchCoreSubjectsForLink();
  }, [tabValue]);


  // Available countries
  const [countries, setCountries] = useState<AdminCountryDTO[]>([]);

  // Available boards for selected country (for form)
  const [boards, setBoards] = useState<AdminBoardDTO[]>([]);
  // All boards for list view lookup
  const [allBoards, setAllBoards] = useState<AdminBoardDTO[]>([]);

  // Curriculum structure view (hierarchy from Scottish service)
  const [curriculumStructure, setCurriculumStructure] = useState<CurriculumStructure | null>(null);
  const [structureLoading, setStructureLoading] = useState(false);
  const [scottishSubjectId, setScottishSubjectId] = useState<number | null>(null);
  const [boardServiceBaseUrl, setBoardServiceBaseUrl] = useState<string | null>(null);

  // Dialogs for managing layers
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [eoDialogOpen, setEoDialogOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState<CurriculumArea | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<CurriculumGroup | null>(null);
  const [selectedEO, setSelectedEO] = useState<ExperienceOutcome | null>(null);
  const [selectedAreaForGroup, setSelectedAreaForGroup] = useState<CurriculumArea | null>(null);
  const [selectedGroupForEO, setSelectedGroupForEO] = useState<CurriculumGroup | null>(null);

  // Form states for layers
  const [formAreaName, setFormAreaName] = useState('');
  const [formAreaDescription, setFormAreaDescription] = useState('');
  const [formAreaDisplayOrder, setFormAreaDisplayOrder] = useState<number | ''>('');
  const [formAreaVisible, setFormAreaVisible] = useState(false);

  const [formGroupName, setFormGroupName] = useState('');
  const [formGroupDescription, setFormGroupDescription] = useState('');
  const [formGroupDisplayOrder, setFormGroupDisplayOrder] = useState<number | ''>('');
  const [formGroupVisible, setFormGroupVisible] = useState(true);

  const [formEOCode, setFormEOCode] = useState('');
  const [formEODescription, setFormEODescription] = useState('');
  const [formEOLevel, setFormEOLevel] = useState('');
  const [formEODisplayOrder, setFormEODisplayOrder] = useState<number | ''>('');
  const [formEOVisible, setFormEOVisible] = useState(true);

  // Collapse/Expand state
  const [expandedAreas, setExpandedAreas] = useState<Set<number>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const scottishLevels = [
    'EARLY',
    'FIRST',
    'SECOND',
    'THIRD_FOURTH',
    'SENIOR_PHASE',
  ];

  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  const api = axios.create({
    baseURL: config.coreApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add request interceptor to include token
  api.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const loadBoardList = async () => {
    try {
      setBoardsLoading(true);
      const data = await getBoards();
      setBoardList(data);
    } catch (err: any) {
      console.error('Error loading boards', err);
      setError(err.response?.data?.message || 'Failed to load boards');
    } finally {
      setBoardsLoading(false);
    }
  };

  useEffect(() => {
    if (isStructureView && curriculumId) {
      loadCurriculumStructure(parseInt(curriculumId));
    } else {
      const init = async () => {
        loadCountries();
        if (tabValue === 0) loadBoardList();
        const boards = await loadAllBoards();
        await loadCurricula(boards);
      };
      init();
    }
  }, [isStructureView, curriculumId]);

  useEffect(() => {
    if (!isStructureView && tabValue === 0) loadBoardList();
  }, [tabValue]);

  const loadCurriculumSubjectsList = async () => {
    try {
      setCurriculumSubjectsLoading(true);
      const boardsForLookup = allBoards.length > 0 ? allBoards : await loadAllBoards();
      const coreSubjects = await getCurriculumSubjects();
      let scottishSubjects: Array<{ id: number; curriculumSubjectId?: number }> = [];
      try {
        const res = await scottishCurriculumManagementApi.getSubjects(scottishBaseUrl);
        scottishSubjects = Array.isArray(res.data) ? res.data : [];
      } catch { /* Scottish service may be down */ }

      const sameId = (a: unknown, b: unknown) =>
        a != null && b != null && Number(a) === Number(b);

      const resolveBoardName = (boardId?: number) => {
        if (boardId == null) return undefined;
        const board = boardsForLookup.find((b) => Number(b.id) === Number(boardId));
        return board?.name || board?.boardType;
      };

      const resolveBoardLinkId = (cs: { id: number; boardId?: number }) => {
        const board = boardsForLookup.find((b) => sameId(b.id, cs.boardId));
        const boardType = board?.boardType?.toUpperCase();
        if (boardType === 'SCOTTISH') {
          const scottish = scottishSubjects.find((s) => sameId(s.curriculumSubjectId, cs.id));
          return scottish?.id;
        }
        // Future: resolve English/Indian board-service ids here
        return undefined;
      };

      const rows: CurrSubjectRow[] = coreSubjects.map((cs: any) => ({
        coreId: cs.id,
        code: cs.code,
        name: cs.name,
        description: cs.description,
        coreSubjectId: cs.coreSubjectId,
        boardId: cs.boardId,
        boardLinkId: resolveBoardLinkId(cs),
        boardName: resolveBoardName(cs.boardId),
      }));
      setCurriculumSubjectsList(rows);

      // Curriculum-subject admins (per-row) + core-subject admins (contact info, read-only).
      const csAdminEntries = await Promise.all(
        rows.map(async (row) => {
          try {
            const admins = await getSubjectAdmins(row.coreId);
            return [row.coreId, admins] as const;
          } catch {
            return [row.coreId, [] as SystemAdminDTO[]] as const;
          }
        })
      );
      setCurriculumSubjectAdmins(Object.fromEntries(csAdminEntries));

      const uniqueCoreSubjectIds = Array.from(
        new Set(rows.map((r) => r.coreSubjectId).filter((x): x is number => typeof x === 'number'))
      );
      const coreAdminEntries = await Promise.all(
        uniqueCoreSubjectIds.map(async (coreSubjectId) => {
          try {
            const admins = await getCoreSubjectAdmins(coreSubjectId);
            return [coreSubjectId, admins] as const;
          } catch {
            return [coreSubjectId, [] as SystemAdminDTO[]] as const;
          }
        })
      );
      setCoreSubjectAdminsByCoreId(Object.fromEntries(coreAdminEntries));
    } catch (err: any) {
      console.error('Error loading curriculum subjects', err);
      setError(err.response?.data?.message || err.message || 'Failed to load curriculum subjects');
      setCurriculumSubjectsList([]);
    } finally {
      setCurriculumSubjectsLoading(false);
    }
  };

  useEffect(() => {
    if (!isStructureView && tabValue === 1 && allBoards.length > 0) loadCurriculumSubjectsList();
  }, [tabValue, allBoards]);

  useEffect(() => {
    if (tabValue !== 1) return;
    let cancelled = false;
    getAllSystemAdmins()
      .then((admins) => { if (!cancelled) setAllSystemAdmins(admins); })
      .catch(() => { /* non-fatal: assign dialog will show empty list */ });
    return () => { cancelled = true; };
  }, [tabValue]);

  const handleOpenCurriculumSubjectAdmins = (row: CurrSubjectRow) => {
    setCsAssignAdminsRow(row);
    const current = curriculumSubjectAdmins[row.coreId] || [];
    setCsAssignSelectedAdminIds(current.map((a) => a.id));
    setCsAssignAdminsDialogOpen(true);
  };

  const handleSaveCurriculumSubjectAdmins = async () => {
    if (!csAssignAdminsRow) return;
    try {
      setCsAssignSaving(true);
      setError(null);
      await assignAdminsToSubject(csAssignAdminsRow.coreId, csAssignSelectedAdminIds);
      setCsAssignAdminsDialogOpen(false);
      setCsAssignAdminsRow(null);
      await loadCurriculumSubjectsList();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save admin assignments');
    } finally {
      setCsAssignSaving(false);
    }
  };

  const resetSubjectForm = () => {
    setFormSubjectName('');
    setFormSubjectCode('');
    setFormSubjectDescription('');
    setFormSubjectCoreId('');
    setFormSubjectBoardId('');
    setSelectedCurriculumSubject(null);
  };

  const handleAddCurriculumSubject = () => {
    resetSubjectForm();
    setSubjectDialogOpen(true);
  };

  const handleEditCurriculumSubject = async (sub: CurrSubjectRow) => {
    setSelectedCurriculumSubject(sub);
    setFormSubjectName(sub.name);
    setFormSubjectCode(sub.code);
    setFormSubjectDescription(sub.description || '');
    setFormSubjectCoreId(sub.coreSubjectId ?? '');
    if (sub.boardId) {
      setFormSubjectBoardId(sub.boardId);
    } else {
      const scottishBoard = allBoards.find(b => b.boardType === 'SCOTTISH');
      setFormSubjectBoardId(scottishBoard?.id ?? '');
    }
    setSubjectDialogOpen(true);
  };

  const handleSubjectMenuOpen = (event: React.MouseEvent<HTMLElement>, sub: CurrSubjectRow) => {
    setAnchorElSubject(event.currentTarget);
    setMenuCurriculumSubject(sub);
  };

  const handleSubjectMenuClose = () => {
    setAnchorElSubject(null);
    setMenuCurriculumSubject(null);
  };

  const handleSaveCurriculumSubject = async () => {
    if (!formSubjectName || !formSubjectCode) {
      setError('Name and code are required');
      return;
    }
    if (!formSubjectCoreId) {
      setError('Core subject is required (add one in Subjects & Topics first)');
      return;
    }
    if (!formSubjectBoardId) {
      setError('Board is required');
      return;
    }
    const selectedBoard = allBoards.find(b => b.id === formSubjectBoardId);
    const boardType = selectedBoard?.boardType;
    if (boardType !== 'SCOTTISH') {
      setError(`Board type "${boardType || 'Unknown'}" is not supported yet`);
      return;
    }
    try {
      setError(null);
      if (selectedCurriculumSubject?.coreId) {
        // Editing an existing curriculum subject
        await updateCurriculumSubject(selectedCurriculumSubject.coreId, {
          coreSubjectId: formSubjectCoreId as number,
          boardId: formSubjectBoardId as number,
          code: formSubjectCode,
          name: formSubjectName,
          description: formSubjectDescription || undefined,
        });

        if (boardType === 'SCOTTISH') {
          await ensureScottishSubjectLink(scottishBaseUrl, selectedCurriculumSubject.coreId);
        }
      } else {
        // Creating brand new: 1) core curriculum_subjects row, 2) board-specific row
        const created = await createCurriculumSubject({
          coreSubjectId: formSubjectCoreId as number,
          boardId: formSubjectBoardId as number,
          code: formSubjectCode,
          name: formSubjectName,
          description: formSubjectDescription || undefined,
        });
        if (boardType === 'SCOTTISH') {
          await ensureScottishSubjectLink(scottishBaseUrl, created.id);
        }
      }
      setSubjectDialogOpen(false);
      resetSubjectForm();
      loadCurriculumSubjectsList();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save curriculum subject');
    }
  };

  const handleDeleteCurriculumSubjectConfirm = async () => {
    if (!menuCurriculumSubject?.coreId) return;
    try {
      setError(null);
      if (menuCurriculumSubject.boardLinkId) {
        await scottishCurriculumManagementApi.deleteSubject(scottishBaseUrl, menuCurriculumSubject.boardLinkId);
      }
      await deleteCurriculumSubject(menuCurriculumSubject.coreId);
      setSubjectDeleteDialogOpen(false);
      setMenuCurriculumSubject(null);
      handleSubjectMenuClose();
      loadCurriculumSubjectsList();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete curriculum subject');
    }
  };

  const loadAllBoards = async (): Promise<AdminBoardDTO[]> => {
    try {
      const data = await getBoards();
      setAllBoards(data);
      return data;
    } catch (err) {
      console.error("Failed to load all boards", err);
      return [];
    }
  };

  const loadCurriculumStructure = async (id: number) => {
    try {
      setStructureLoading(true);
      setError(null);

      // 1. Fetch curriculum metadata (includes country/board names for header)
      const metadataResponse = await api.get(`/api/admin/courses/curriculums/${id}`);
      if (!metadataResponse.data.success) {
        throw new Error(metadataResponse.data.message || 'Failed to load curriculum metadata');
      }
      let curriculum: Curriculum = metadataResponse.data.data;

      // Enrich Scottish CfE level (same as list view; not returned by core metadata alone)
      try {
        const scottishRes = await scottishCurriculumManagementApi.getCurriculumByCoreId(scottishBaseUrl, id);
        if (scottishRes.data?.success && scottishRes.data.data) {
          curriculum = {
            ...curriculum,
            level: curriculum.level || scottishRes.data.data.level,
            scottishLevel: curriculum.level || scottishRes.data.data.level,
            scottishCurriculumId: scottishRes.data.data.id,
          };
        }
      } catch {
        /* Scottish record may not exist yet */
      }

      // 2. Fetch hierarchy from Scottish curriculum service (via core service-info)
      const boardData = await getCurriculumHierarchyFromBoardService(curriculum.id);

      // 3. Map EOHierarchyDTO to Frontend Interface
      const areas: CurriculumArea[] = boardData.hierarchy.map((dto) => ({
        id: dto.areaId,
        name: dto.areaName,
        description: dto.areaDescription,
        displayOrder: dto.areaDisplayOrder,
        defaultVisibleToStudents: true,
        groups: (dto.groups || []).map((gDto) => ({
          id: gDto.groupId,
          name: gDto.groupName,
          description: gDto.groupDescription,
          displayOrder: gDto.groupDisplayOrder,
          defaultVisibleToStudents: true,
          experienceOutcomes: (gDto.experienceOutcomes || []).map((eoDto) => ({
            id: eoDto.eoId,
            code: eoDto.code,
            description: eoDto.description,
            level: eoDto.level,
            displayOrder: eoDto.displayOrder,
            defaultVisibleToStudents: true
          }))
        }))
      }));

      setCurriculumStructure({
        curriculum: curriculum,
        areas: areas
      });
      setScottishSubjectId(boardData.scottishSubjectId ?? null);
      setBoardServiceBaseUrl(boardData.boardServiceBaseUrl ?? null);

    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load curriculum structure');
      console.error('Error loading curriculum structure:', err);
    } finally {
      setStructureLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const countriesData = await getAllCountries();
      setCountries(countriesData);
    } catch (err: any) {
      console.error('Error loading countries:', err);
      // Don't show error to user - countries are optional for now
    }
  };

  const loadBoardsForCountry = async (countryId: number) => {
    if (!countryId) {
      setBoards([]);
      return;
    }
    try {
      const data = await getBoards(countryId);
      setBoards(data);
    } catch (err: any) {
      console.error('Error loading boards:', err);
      setBoards([]);
    }
  };

  const loadCurricula = async (boardsOverride?: AdminBoardDTO[]) => {
    const boardsRef = boardsOverride ?? allBoards;
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/admin/courses/curriculums');
      if (response.data.success) {
        const coreCurricula: Curriculum[] = response.data.data;
        const enriched = await Promise.all(coreCurricula.map(async (c) => {
          try {
            const board = boardsRef.find(b => b.id === c.boardId);
            if (board?.boardType?.toUpperCase() === 'SCOTTISH') {
              const scottishRes = await scottishCurriculumManagementApi.getCurriculumByCoreId(scottishBaseUrl, c.id);
              if (scottishRes.data?.success && scottishRes.data.data) {
                return {
                  ...c,
                  level: c.level || scottishRes.data.data.level,
                  scottishLevel: c.level || scottishRes.data.data.level,
                  scottishCurriculumId: scottishRes.data.data.id,
                };
              }
            }
          } catch { /* Scottish record may not exist yet */ }
          return c;
        }));
        setCurricula(enriched);
      } else {
        setError(response.data.message || 'Failed to load curricula');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load curricula');
      console.error('Error loading curricula:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetBoardForm = () => {
    setFormBoardName('');
    setFormBoardCode('');
    setFormBoardCountryId('');
    setFormBoardType('SCOTTISH');
    setFormBoardLevel('NATIONAL');
    setSelectedBoard(null);
  };

  const handleAddBoard = () => {
    resetBoardForm();
    setBoardDialogOpen(true);
  };

  const handleEditBoard = (board: AdminBoardDTO) => {
    setSelectedBoard(board);
    setFormBoardName(board.name);
    setFormBoardCode(board.code || '');
    setFormBoardCountryId(board.countryId ?? '');
    setFormBoardType(board.boardType || 'SCOTTISH');
    setFormBoardLevel(board.level || 'NATIONAL');
    setBoardDialogOpen(true);
  };

  const handleBoardMenuOpen = (event: React.MouseEvent<HTMLElement>, board: AdminBoardDTO) => {
    setAnchorElBoard(event.currentTarget);
    setMenuBoard(board);
  };

  const handleBoardMenuClose = () => {
    setAnchorElBoard(null);
    setMenuBoard(null);
  };

  const handleSaveBoard = async () => {
    if (!formBoardName || !formBoardCode || !formBoardCountryId) {
      setError('Name, code and country are required');
      return;
    }
    try {
      setError(null);
      const payload: AdminBoardDTO = {
        name: formBoardName,
        code: formBoardCode,
        countryId: formBoardCountryId as number,
        boardType: formBoardType,
        level: formBoardLevel,
      };
      if (selectedBoard?.id) {
        await updateBoard(selectedBoard.id, payload);
      } else {
        await createBoard(payload);
      }
      setBoardDialogOpen(false);
      resetBoardForm();
      loadBoardList();
      loadAllBoards();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save board');
    }
  };

  const handleDeleteBoardConfirm = async () => {
    if (!menuBoard?.id) return;
    try {
      setError(null);
      await deleteBoard(menuBoard.id);
      setBoardDeleteDialogOpen(false);
      setMenuBoard(null);
      handleBoardMenuClose();
      loadBoardList();
      loadAllBoards();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete board');
    }
  };

  const handleAddNew = () => {
    setSelectedCurriculum(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleEdit = async (curriculum: Curriculum) => {
    setSelectedCurriculum(curriculum);
    setFormCountryId(curriculum.countryId);
    setFormVersion(curriculum.version || '');
    setFormDescription(curriculum.description || '');
    setFormScottishLevel(curriculum.level || curriculum.scottishLevel || '');
    await loadBoardsForCountry(curriculum.countryId);
    setFormCurrBoardId(curriculum.boardId);
    await loadCurrSubjectsForBoard(curriculum.boardId);
    setFormCurrSubjectId(curriculum.curriculumSubjectId);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!menuCurriculum) return;

    try {
      if (menuCurriculum.scottishCurriculumId) {
        try {
          await scottishCurriculumManagementApi.deleteCurriculum(scottishBaseUrl, menuCurriculum.scottishCurriculumId);
        } catch { /* Scottish record may not exist */ }
      }
      await api.delete(`/api/admin/courses/curriculums/${menuCurriculum.id}`);
      await loadCurricula();
      setDeleteDialogOpen(false);
      setMenuCurriculum(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete curriculum');
      console.error('Error deleting curriculum:', err);
    }
  };

  const handleActivate = async (curriculum: Curriculum) => {
    try {
      await api.put(`/api/admin/courses/curriculums/${curriculum.id}/activate`);
      await loadCurricula();
      handleMenuClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to activate curriculum');
      console.error('Error activating curriculum:', err);
    }
  };

  const handleArchive = async (curriculum: Curriculum) => {
    try {
      await api.put(`/api/admin/courses/curriculums/${curriculum.id}/archive`);
      await loadCurricula();
      handleMenuClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to archive curriculum');
      console.error('Error archiving curriculum:', err);
    }
  };

  const handleViewStructure = (curriculum: Curriculum) => {
    navigate(`/admin/curricula/${curriculum.id}/structure`);
    handleMenuClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, curriculum: Curriculum) => {
    setAnchorEl(event.currentTarget);
    setMenuCurriculum(curriculum);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCurriculum(null);
  };

  const resetForm = () => {
    setFormCountryId('');
    setFormCurrBoardId('');
    setFormCurrSubjectId('');
    setFormVersion('');
    setFormDescription('');
    setFormScottishLevel('');
    setBoards([]);
    setFilteredCurrSubjects([]);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedCurriculum(null);
    resetForm();
  };

  const loadCurrSubjectsForBoard = async (boardId: number | '') => {
    if (!boardId) {
      setFilteredCurrSubjects([]);
      return;
    }
    try {
      const allSubjects = await getCurriculumSubjects();
      const filtered = allSubjects
        .filter((s: any) => s.boardId != null && Number(s.boardId) === Number(boardId))
        .map((s: any) => ({ id: s.id, name: s.name, code: s.code }));
      setFilteredCurrSubjects(filtered);
    } catch (err) {
      console.error('Error loading curriculum subjects for board:', err);
      setFilteredCurrSubjects([]);
    }
  };

  const handleSubmit = async () => {
    if (!formCountryId || !formCurrBoardId || !formCurrSubjectId) {
      setError('Please fill in all required fields');
      return;
    }
    const board = allBoards.find(b => b.id === formCurrBoardId);
    const boardType = board?.boardType?.toUpperCase();
    if (boardType === 'SCOTTISH' && !formScottishLevel) {
      setError('Level is required for Scottish curricula');
      return;
    }

    try {
      const corePayload = {
        countryId: Number(formCountryId),
        boardId: Number(formCurrBoardId),
        curriculumSubjectId: Number(formCurrSubjectId),
        version: formVersion || undefined,
        level: boardType === 'SCOTTISH' ? formScottishLevel : undefined,
        description: formDescription || undefined,
      };

      let coreCurriculumId: number;

      if (selectedCurriculum) {
        const res = await api.put(`/api/admin/courses/curriculums/${selectedCurriculum.id}`, corePayload);
        coreCurriculumId = res.data.data.id;
      } else {
        const res = await api.post('/api/admin/courses/curriculums', corePayload);
        coreCurriculumId = res.data.data.id;
      }

      if (boardType === 'SCOTTISH') {
        await ensureScottishSubjectLink(scottishBaseUrl, Number(formCurrSubjectId));

        const scottishPayload = { curriculumId: coreCurriculumId, level: formScottishLevel };
        if (selectedCurriculum?.scottishCurriculumId) {
          await scottishCurriculumManagementApi.updateCurriculum(
            scottishBaseUrl,
            selectedCurriculum.scottishCurriculumId,
            scottishPayload
          );
        } else {
          try {
            await scottishCurriculumManagementApi.createCurriculum(scottishBaseUrl, scottishPayload);
          } catch (createErr: any) {
            // Row may already exist in Postgres while the list lost scottishCurriculumId (e.g. prior H2 deploy).
            if (createErr.response?.status !== 400) throw createErr;
            const existing = await scottishCurriculumManagementApi.getCurriculumByCoreId(scottishBaseUrl, coreCurriculumId);
            if (!existing.data?.data?.id) throw createErr;
            await scottishCurriculumManagementApi.updateCurriculum(
              scottishBaseUrl,
              existing.data.data.id,
              scottishPayload
            );
          }
        }
      }

      await loadCurricula();
      handleDialogClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save curriculum');
      console.error('Error saving curriculum:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DRAFT':
        return 'warning';
      case 'ARCHIVED':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatLevel = (level?: string | null) => {
    if (!level) return '—';
    return level.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Form reset functions
  const resetAreaForm = () => {
    setFormAreaName('');
    setFormAreaDescription('');
    setFormAreaDisplayOrder('');
    setFormAreaVisible(false);
  };

  const resetGroupForm = () => {
    setFormGroupName('');
    setFormGroupDescription('');
    setFormGroupDisplayOrder('');
    setFormGroupVisible(true);
  };

  const resetEOForm = () => {
    setFormEOCode('');
    setFormEODescription('');
    setFormEOLevel('');
    setFormEODisplayOrder('');
    setFormEOVisible(true);
  };

  // Save handlers – all write operations go to Scottish curriculum service, not core
  const handleSaveArea = async () => {
    if (!curriculumStructure || !curriculumId || !formAreaName) return;
    const baseUrl = boardServiceBaseUrl;
    const subjectId = scottishSubjectId;
    if (!baseUrl || subjectId == null) {
      setError('Structure editing is only available for Scottish curriculum. Ensure the Scottish curriculum service is configured.');
      return;
    }

    try {
      setStructureLoading(true);
      setError(null);
      const request = {
        name: formAreaName,
        description: formAreaDescription || undefined,
        displayOrder: formAreaDisplayOrder ? Number(formAreaDisplayOrder) : undefined,
      };

      if (selectedArea) {
        await scottishCurriculumManagementApi.updateArea(baseUrl, selectedArea.id, request);
      } else {
        await scottishCurriculumManagementApi.createArea(baseUrl, subjectId, request);
      }

      setAreaDialogOpen(false);
      resetAreaForm();
      await loadCurriculumStructure(parseInt(curriculumId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save area');
      console.error('Error saving area:', err);
    } finally {
      setStructureLoading(false);
    }
  };

  const handleSaveGroup = async () => {
    if (!curriculumStructure || !curriculumId || !formGroupName) return;
    if (!selectedGroup && !selectedAreaForGroup) return;
    const baseUrl = boardServiceBaseUrl;
    if (!baseUrl) {
      setError('Structure editing is only available for Scottish curriculum. Ensure the Scottish curriculum service is configured.');
      return;
    }

    try {
      setStructureLoading(true);
      setError(null);
      const request = {
        name: formGroupName,
        description: formGroupDescription || undefined,
        displayOrder: formGroupDisplayOrder ? Number(formGroupDisplayOrder) : undefined,
      };

      if (selectedGroup) {
        await scottishCurriculumManagementApi.updateGroup(baseUrl, selectedGroup.id, request);
      } else if (selectedAreaForGroup) {
        await scottishCurriculumManagementApi.createGroup(baseUrl, selectedAreaForGroup.id, request);
      }

      setGroupDialogOpen(false);
      resetGroupForm();
      await loadCurriculumStructure(parseInt(curriculumId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save group');
      console.error('Error saving group:', err);
    } finally {
      setStructureLoading(false);
    }
  };

  const handleSaveEO = async () => {
    if (!curriculumStructure || !curriculumId || !formEOCode || !formEODescription) return;
    if (!selectedEO && !selectedGroupForEO) return;
    const baseUrl = boardServiceBaseUrl;
    if (!baseUrl) {
      setError('Structure editing is only available for Scottish curriculum. Ensure the Scottish curriculum service is configured.');
      return;
    }

    try {
      setStructureLoading(true);
      setError(null);
      const request = {
        code: formEOCode,
        description: formEODescription,
        level: formEOLevel || undefined,
        displayOrder: formEODisplayOrder ? Number(formEODisplayOrder) : undefined,
      };

      if (selectedEO) {
        await scottishCurriculumManagementApi.updateOutcome(baseUrl, selectedEO.id, request);
      } else if (selectedGroupForEO) {
        await scottishCurriculumManagementApi.createOutcome(baseUrl, selectedGroupForEO.id, request);
      }

      setEoDialogOpen(false);
      resetEOForm();
      await loadCurriculumStructure(parseInt(curriculumId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save experience outcome');
      console.error('Error saving experience outcome:', err);
    } finally {
      setStructureLoading(false);
    }
  };

  // Delete handlers – Scottish curriculum service
  const handleDeleteArea = async (areaId: number) => {
    if (!curriculumId || !window.confirm('Are you sure you want to delete this area? This will also delete all groups and EOs under it.')) return;
    const baseUrl = boardServiceBaseUrl;
    if (!baseUrl) {
      setError('Structure editing is only available for Scottish curriculum.');
      return;
    }
    try {
      setStructureLoading(true);
      setError(null);
      await scottishCurriculumManagementApi.deleteArea(baseUrl, areaId);
      await loadCurriculumStructure(parseInt(curriculumId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete area');
      console.error('Error deleting area:', err);
    } finally {
      setStructureLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!curriculumId || !window.confirm('Are you sure you want to delete this group? This will also delete all EOs under it.')) return;
    const baseUrl = boardServiceBaseUrl;
    if (!baseUrl) {
      setError('Structure editing is only available for Scottish curriculum.');
      return;
    }
    try {
      setStructureLoading(true);
      setError(null);
      await scottishCurriculumManagementApi.deleteGroup(baseUrl, groupId);
      await loadCurriculumStructure(parseInt(curriculumId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete group');
      console.error('Error deleting group:', err);
    } finally {
      setStructureLoading(false);
    }
  };

  const handleDeleteEO = async (eoId: number) => {
    if (!curriculumId || !window.confirm('Are you sure you want to delete this experience outcome?')) return;
    const baseUrl = boardServiceBaseUrl;
    if (!baseUrl) {
      setError('Structure editing is only available for Scottish curriculum.');
      return;
    }
    try {
      setStructureLoading(true);
      setError(null);
      await scottishCurriculumManagementApi.deleteOutcome(baseUrl, eoId);
      await loadCurriculumStructure(parseInt(curriculumId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete EO');
      console.error('Error deleting EO:', err);
    } finally {
      setStructureLoading(false);
    }
  };



  // Render structure view
  if (isStructureView) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/curricula')}
              sx={{ mr: 2 }}
            >
              ← Back to Curricula
            </Button>
            <Typography variant="h4" component="h1">
              Curriculum Structure
            </Typography>
          </Box>
          {curriculumStructure && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<UnfoldMoreIcon />}
                onClick={() => {
                  // Expand all areas
                  if (curriculumStructure.areas) {
                    const allAreaIds = new Set(curriculumStructure.areas.map(a => a.id));
                    setExpandedAreas(allAreaIds);
                    // Also expand all groups
                    const allGroupIds = new Set<number>();
                    curriculumStructure.areas.forEach(area => {
                      if (area.groups) {
                        area.groups.forEach(group => allGroupIds.add(group.id));
                      }
                    });
                    setExpandedGroups(allGroupIds);
                  }
                }}
              >
                Expand All
              </Button>
              <Button
                variant="outlined"
                startIcon={<UnfoldLessIcon />}
                onClick={() => {
                  // Collapse all
                  setExpandedAreas(new Set());
                  setExpandedGroups(new Set());
                }}
              >
                Collapse All
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedArea(null);
                  resetAreaForm();
                  setAreaDialogOpen(true);
                }}
              >
                Add Area
              </Button>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {structureLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : curriculumStructure ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              {curriculumStructure.curriculum.subjectName || `ID ${curriculumStructure.curriculum.curriculumSubjectId}`} - {formatLevel(curriculumStructure.curriculum.scottishLevel)}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {curriculumStructure.curriculum.countryName || curriculumStructure.curriculum.countryId || '—'} - {curriculumStructure.curriculum.boardName || curriculumStructure.curriculum.boardId || '—'}
              {curriculumStructure.curriculum.version && ` (${curriculumStructure.curriculum.version})`}
            </Typography>
            {curriculumStructure.curriculum.description && (
              <Typography variant="body2" sx={{ mt: 1, mb: 3 }}>
                {curriculumStructure.curriculum.description}
              </Typography>
            )}

            {curriculumStructure.areas && curriculumStructure.areas.length > 0 ? (
              <Box sx={{ mt: 3 }}>
                {curriculumStructure.areas
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .map((area) => (
                    <Box key={area.id} sx={{ mb: 3 }}>
                      <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.100' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newExpanded = new Set(expandedAreas);
                                if (newExpanded.has(area.id)) {
                                  newExpanded.delete(area.id);
                                  // Also collapse all groups in this area
                                  const newExpandedGroups = new Set(expandedGroups);
                                  if (area.groups) {
                                    area.groups.forEach(group => newExpandedGroups.delete(group.id));
                                  }
                                  setExpandedGroups(newExpandedGroups);
                                } else {
                                  newExpanded.add(area.id);
                                }
                                setExpandedAreas(newExpanded);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              {expandedAreas.has(area.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {area.name}
                              </Typography>
                              {area.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {area.description}
                                </Typography>
                              )}
                              <Chip
                                label={area.defaultVisibleToStudents ? 'Visible to Students' : 'Hidden from Students'}
                                color={area.defaultVisibleToStudents ? 'success' : 'default'}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedArea(area);
                                setFormAreaName(area.name);
                                setFormAreaDescription(area.description || '');
                                setFormAreaDisplayOrder(area.displayOrder || '');
                                setFormAreaVisible(area.defaultVisibleToStudents);
                                setAreaDialogOpen(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteArea(area.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                setSelectedAreaForGroup(area);
                                setSelectedGroup(null);
                                resetGroupForm();
                                setGroupDialogOpen(true);
                              }}
                              variant="outlined"
                            >
                              Add Group
                            </Button>
                          </Box>
                        </Box>
                      </Paper>

                      {expandedAreas.has(area.id) && area.groups && area.groups.length > 0 && (
                        <Box sx={{ ml: 3, mt: 2 }}>
                          {area.groups
                            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                            .map((group) => (
                              <Box key={group.id} sx={{ mb: 2 }}>
                                <Paper elevation={0} sx={{ p: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const newExpanded = new Set(expandedGroups);
                                          if (newExpanded.has(group.id)) {
                                            newExpanded.delete(group.id);
                                          } else {
                                            newExpanded.add(group.id);
                                          }
                                          setExpandedGroups(newExpanded);
                                        }}
                                        sx={{ p: 0.5 }}
                                      >
                                        {expandedGroups.has(group.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                      </IconButton>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        {group.name}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      {group.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                          {group.description}
                                        </Typography>
                                      )}
                                      <Chip
                                        label={group.defaultVisibleToStudents ? 'Visible' : 'Hidden'}
                                        color={group.defaultVisibleToStudents ? 'success' : 'default'}
                                        size="small"
                                        sx={{ mt: 1 }}
                                      />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setSelectedGroup(group);
                                          setFormGroupName(group.name);
                                          setFormGroupDescription(group.description || '');
                                          setFormGroupDisplayOrder(group.displayOrder || '');
                                          setFormGroupVisible(group.defaultVisibleToStudents);
                                          setGroupDialogOpen(true);
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteGroup(group.id)}
                                        color="error"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                      <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                          setSelectedGroupForEO(group);
                                          setSelectedEO(null);
                                          resetEOForm();
                                          setEoDialogOpen(true);
                                        }}
                                        variant="outlined"
                                      >
                                        Add EO
                                      </Button>
                                    </Box>
                                  </Box>

                                  {expandedGroups.has(group.id) && group.experienceOutcomes && group.experienceOutcomes.length > 0 && (
                                    <Box sx={{ ml: 2, mt: 2 }}>
                                      {group.experienceOutcomes
                                        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                                        .map((eo) => (
                                          <Box key={eo.id} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                              <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                  {eo.code}: {eo.description}
                                                </Typography>
                                                {eo.level && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    Level: {eo.level}
                                                  </Typography>
                                                )}
                                                <Chip
                                                  label={eo.defaultVisibleToStudents ? 'Visible' : 'Hidden'}
                                                  color={eo.defaultVisibleToStudents ? 'success' : 'default'}
                                                  size="small"
                                                  sx={{ mt: 0.5 }}
                                                />
                                              </Box>
                                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => {
                                                    setSelectedEO(eo);
                                                    setFormEOCode(eo.code);
                                                    setFormEODescription(eo.description);
                                                    setFormEOLevel(eo.level || '');
                                                    setFormEODisplayOrder(eo.displayOrder || '');
                                                    setFormEOVisible(eo.defaultVisibleToStudents);
                                                    setEoDialogOpen(true);
                                                  }}
                                                >
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleDeleteEO(eo.id)}
                                                  color="error"
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </Box>
                                            </Box>
                                          </Box>
                                        ))}
                                    </Box>
                                  )}
                                </Paper>
                              </Box>
                            ))}
                        </Box>
                      )}
                    </Box>
                  ))}
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 3 }}>
                No curriculum structure found. Please add areas, groups, and experience outcomes.
              </Alert>
            )}
          </Paper>
        ) : (
          <Alert severity="warning">
            Curriculum structure not found.
          </Alert>
        )}
        {/* Area Dialog */}
        <Dialog open={areaDialogOpen} onClose={() => setAreaDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedArea ? 'Edit Area' : 'Add New Area'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Area Name"
                value={formAreaName}
                onChange={(e) => setFormAreaName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={formAreaDescription}
                onChange={(e) => setFormAreaDescription(e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
              <TextField
                label="Display Order"
                type="number"
                value={formAreaDisplayOrder}
                onChange={(e) => setFormAreaDisplayOrder(e.target.value ? parseInt(e.target.value) : '')}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formAreaVisible}
                    onChange={(e) => setFormAreaVisible(e.target.checked)}
                  />
                }
                label="Visible to Students"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAreaDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveArea} variant="contained" disabled={!formAreaName}>
              {selectedArea ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Group Dialog */}
        <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedGroup ? 'Edit Group' : 'Add New Group'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Group Name"
                value={formGroupName}
                onChange={(e) => setFormGroupName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={formGroupDescription}
                onChange={(e) => setFormGroupDescription(e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
              <TextField
                label="Display Order"
                type="number"
                value={formGroupDisplayOrder}
                onChange={(e) => setFormGroupDisplayOrder(e.target.value ? parseInt(e.target.value) : '')}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formGroupVisible}
                    onChange={(e) => setFormGroupVisible(e.target.checked)}
                  />
                }
                label="Visible to Students"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveGroup} variant="contained" disabled={!formGroupName}>
              {selectedGroup ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Experience Outcome Dialog */}
        <Dialog open={eoDialogOpen} onClose={() => setEoDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedEO ? 'Edit Experience Outcome' : 'Add New Experience Outcome'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="EO Code (e.g., MNU 3-01a)"
                value={formEOCode}
                onChange={(e) => setFormEOCode(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={formEODescription}
                onChange={(e) => setFormEODescription(e.target.value)}
                required
                multiline
                rows={3}
                fullWidth
              />
              <TextField
                label="Level (e.g., Third Level)"
                value={formEOLevel}
                onChange={(e) => setFormEOLevel(e.target.value)}
                fullWidth
              />
              <TextField
                label="Display Order"
                type="number"
                value={formEODisplayOrder}
                onChange={(e) => setFormEODisplayOrder(e.target.value ? parseInt(e.target.value) : '')}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formEOVisible}
                    onChange={(e) => setFormEOVisible(e.target.checked)}
                  />
                }
                label="Visible to Students"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEoDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEO} variant="contained" disabled={!formEOCode || !formEODescription}>
              {selectedEO ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // Render list view
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Boards &amp; Curriculum
        </Typography>
        {tabValue === 0 ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddBoard}>
            Add Board
          </Button>
        ) : tabValue === 1 ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCurriculumSubject}>
            Add Curriculum Subject
          </Button>
        ) : (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
            Add New Curriculum
          </Button>
        )}
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Boards" />
        <Tab label="Curriculum Subjects" />
        <Tab label="Course Curricula" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tabValue === 0 ? (
        boardsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Boards are the qualification authorities under each country and they scope every curriculum subject and course record you create for that board type.
              Consistent names, codes, and board types keep later setup predictable for admins and avoid mismatched links to board-specific services.
              Treat edits carefully because anything that references a board will rely on this record staying accurate.
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Board Type</TableCell>
                    <TableCell>Level</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boardList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No boards found. Click &quot;Add Board&quot; to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    boardList.map((board) => (
                      <TableRow key={board.id} hover>
                        <TableCell>{board.name}</TableCell>
                        <TableCell>{board.code || '-'}</TableCell>
                        <TableCell>{board.countryName || board.countryId}</TableCell>
                        <TableCell>{board.boardType || '-'}</TableCell>
                        <TableCell>{board.level || '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleBoardMenuOpen(e, board)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )
      ) : null}

      {tabValue === 1 ? (
        <>
          {curriculumSubjectsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Curriculum subjects bridge each core subject to a board so students follow the right curriculum for that board.
                The Board ID column shows the board-specific service record (e.g. Scottish curriculum subject id) used for structure and outcomes.
                When you add or save changes here, the main catalog and the board curriculum service stay aligned.
              </Typography>
              <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name / Code</TableCell>
                    <TableCell>Parent core subject</TableCell>
                    <TableCell>Board</TableCell>
                    <TableCell>Curriculum subject admins</TableCell>
                    <TableCell>Core subject admins<br/><Typography variant="caption" color="text.secondary">contact for missing topics</Typography></TableCell>
                    <TableCell>Board ID</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {curriculumSubjectsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No curriculum subjects. Add one by choosing a core subject, a board, and a name/code.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    curriculumSubjectsList.map((sub) => {
                      const coreSubjectName = sub.coreSubjectId != null
                        ? coreSubjectsForLink.find(c => c.id === sub.coreSubjectId)?.name
                        : undefined;
                      const csAdmins = curriculumSubjectAdmins[sub.coreId] || [];
                      const coreAdmins = sub.coreSubjectId != null
                        ? (coreSubjectAdminsByCoreId[sub.coreSubjectId] || [])
                        : [];
                      return (
                        <TableRow key={sub.coreId} hover>
                          <TableCell>{sub.coreId}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{sub.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{sub.code}</Typography>
                          </TableCell>
                          <TableCell>
                            {coreSubjectName ? (
                              <>
                                <Typography variant="body2">{coreSubjectName}</Typography>
                                <Typography variant="caption" color="text.secondary">id {sub.coreSubjectId}</Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">unlinked</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {sub.boardName
                              || (sub.boardId != null
                                ? allBoards.find((b) => Number(b.id) === Number(sub.boardId))?.name
                                  || allBoards.find((b) => Number(b.id) === Number(sub.boardId))?.boardType
                                : undefined)
                              || '—'}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              {csAdmins.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" fontStyle="italic">none</Typography>
                              ) : (
                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                  {csAdmins.map((a) => (
                                    <Chip
                                      key={a.id}
                                      size="small"
                                      label={`${a.firstName} ${a.lastName}`}
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              )}
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleOpenCurriculumSubjectAdmins(sub)}
                                sx={{ alignSelf: 'flex-start', textTransform: 'none', p: 0, minWidth: 0 }}
                              >
                                Manage
                              </Button>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {coreAdmins.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">none</Typography>
                            ) : (
                              <Box display="flex" flexDirection="column" gap={0.25}>
                                {coreAdmins.map((a) => (
                                  <Typography key={a.id} variant="caption">
                                    {a.firstName} {a.lastName} <Typography component="span" variant="caption" color="text.secondary">&lt;{a.email}&gt;</Typography>
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{sub.boardLinkId ?? '—'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={(e) => handleSubjectMenuOpen(e, sub)}>
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </>
      ) : null}

      {tabValue === 2 ? (loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Course curricula are the versioned records you bind to a country, board, and curriculum subject so teaching uses the right syllabus and metadata. Use them to open structure, track versions, capture a short description, and set Scottish CfE level when that board applies. Activate or archive records here so admins and students see the correct lifecycle state without guessing which row is current.
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Core Curriculum ID</TableCell>
                  <TableCell>Scottish ID</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Board</TableCell>
                  <TableCell>Curriculum Subject</TableCell>
                  <TableCell>Level / Class Year</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {curricula.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No curricula found. Click "Add New Curriculum" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  curricula.map((curriculum) => (
                    <TableRow key={curriculum.id} hover>
                      <TableCell>{curriculum.id}</TableCell>
                      <TableCell>{curriculum.scottishCurriculumId ?? '—'}</TableCell>
                      <TableCell>{curriculum.countryName || countries.find(c => c.id === curriculum.countryId)?.name || curriculum.countryId}</TableCell>
                      <TableCell>{curriculum.boardName || allBoards.find(b => b.id === curriculum.boardId)?.name || curriculum.boardId}</TableCell>
                      <TableCell>{curriculum.subjectName || `ID ${curriculum.curriculumSubjectId}`}</TableCell>
                      <TableCell>{curriculum.scottishLevel ? formatLevel(curriculum.scottishLevel) : '—'}</TableCell>
                      <TableCell>{curriculum.version || '—'}</TableCell>
                      <TableCell>{curriculum.description || '—'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, curriculum)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )) : null}

      {/* Board Actions Menu */}
      <Menu anchorEl={anchorElBoard} open={Boolean(anchorElBoard)} onClose={handleBoardMenuClose}>
        <MenuItem onClick={() => { handleEditBoard(menuBoard!); handleBoardMenuClose(); }}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => { setBoardDeleteDialogOpen(true); handleBoardMenuClose(); }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewStructure(menuCurriculum!)}>
          <VisibilityIcon sx={{ mr: 1 }} /> View Structure
        </MenuItem>
        <MenuItem onClick={() => handleEdit(menuCurriculum!)}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        {menuCurriculum?.status === 'DRAFT' && (
          <MenuItem onClick={() => handleActivate(menuCurriculum!)}>
            <CheckCircleIcon sx={{ mr: 1 }} /> Activate
          </MenuItem>
        )}
        {menuCurriculum?.status === 'ACTIVE' && (
          <MenuItem onClick={() => handleArchive(menuCurriculum!)}>
            <ArchiveIcon sx={{ mr: 1 }} /> Archive
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Board Add/Edit Dialog */}
      <Dialog open={boardDialogOpen} onClose={() => { setBoardDialogOpen(false); resetBoardForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedBoard ? 'Edit Board' : 'Add Board'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField label="Name" value={formBoardName} onChange={(e) => setFormBoardName(e.target.value)} required fullWidth />
            <TextField label="Code" value={formBoardCode} onChange={(e) => setFormBoardCode(e.target.value)} required fullWidth placeholder="e.g. SCE" />
            <FormControl fullWidth required>
              <InputLabel>Country</InputLabel>
              <Select
                value={formBoardCountryId}
                label="Country"
                onChange={(e) => setFormBoardCountryId(Number(e.target.value))}
              >
                {countries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Board Type</InputLabel>
              <Select value={formBoardType} label="Board Type" onChange={(e) => setFormBoardType(e.target.value)}>
                <MenuItem value="SCOTTISH">Scottish</MenuItem>
                <MenuItem value="ENGLISH">English</MenuItem>
                <MenuItem value="INDIAN">Indian</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Level</InputLabel>
              <Select value={formBoardLevel} label="Level" onChange={(e) => setFormBoardLevel(e.target.value)}>
                <MenuItem value="NATIONAL">National</MenuItem>
                <MenuItem value="STATE">State</MenuItem>
                <MenuItem value="COUNCIL">Council</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBoardDialogOpen(false); resetBoardForm(); }}>Cancel</Button>
          <Button onClick={handleSaveBoard} variant="contained" disabled={!formBoardName || !formBoardCode || !formBoardCountryId}>
            {selectedBoard ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Board Delete Confirmation */}
      <Dialog open={boardDeleteDialogOpen} onClose={() => setBoardDeleteDialogOpen(false)}>
        <DialogTitle>Delete Board</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the board &quot;{menuBoard?.name}&quot;? This will fail if any curriculum uses this board.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBoardDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteBoardConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Curriculum Subject Actions Menu */}
      <Menu anchorEl={anchorElSubject} open={Boolean(anchorElSubject)} onClose={handleSubjectMenuClose}>
        <MenuItem onClick={() => { handleEditCurriculumSubject(menuCurriculumSubject!); handleSubjectMenuClose(); }}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => { setSubjectDeleteDialogOpen(true); handleSubjectMenuClose(); }} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Curriculum Subject Add/Edit Dialog */}
      <Dialog open={subjectDialogOpen} onClose={() => { setSubjectDialogOpen(false); resetSubjectForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedCurriculumSubject ? 'Edit Curriculum Subject' : 'Add Curriculum Subject'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a core subject and board, then provide the curriculum subject name and code.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Core Subject</InputLabel>
              <Select value={formSubjectCoreId} label="Core Subject" onChange={(e) => setFormSubjectCoreId(Number(e.target.value))} disabled={!!selectedCurriculumSubject?.coreId}>
                {coreSubjectsForLink.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Board</InputLabel>
              <Select value={formSubjectBoardId} label="Board" onChange={(e) => setFormSubjectBoardId(Number(e.target.value))}>
                {allBoards.map((b) => (
                  <MenuItem key={b.id} value={b.id!}>{b.name} ({b.boardType || 'Unknown'})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Name" value={formSubjectName} onChange={(e) => setFormSubjectName(e.target.value)} required fullWidth placeholder="e.g. Mathematics & Numeracy" />
            <TextField label="Code" value={formSubjectCode} onChange={(e) => setFormSubjectCode(e.target.value)} required fullWidth placeholder="e.g. MNU" />
            <TextField label="Description" value={formSubjectDescription} onChange={(e) => setFormSubjectDescription(e.target.value)} multiline rows={2} fullWidth />
            {formSubjectBoardId && (() => {
              const selectedBoard = allBoards.find(b => b.id === formSubjectBoardId);
              const boardType = selectedBoard?.boardType;
              if (boardType !== 'SCOTTISH') {
                return (
                  <Alert severity="info">
                    Only Scottish boards are supported at this time. Support for {boardType || 'this board type'} is coming soon.
                  </Alert>
                );
              }
              return null;
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSubjectDialogOpen(false); resetSubjectForm(); }}>Cancel</Button>
          <Button
            onClick={handleSaveCurriculumSubject}
            variant="contained"
            disabled={!formSubjectCoreId || !formSubjectBoardId || !formSubjectName || !formSubjectCode || allBoards.find(b => b.id === formSubjectBoardId)?.boardType !== 'SCOTTISH'}
          >
            {selectedCurriculumSubject ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Curriculum Subject Admin Assign Dialog */}
      <Dialog
        open={csAssignAdminsDialogOpen}
        onClose={() => { setCsAssignAdminsDialogOpen(false); setCsAssignAdminsRow(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Curriculum subject admins — {csAssignAdminsRow?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These admins manage the per-board curriculum (boards, EOs, picking topics from the
            parent core subject). They are a separate population from core-subject admins.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {allSystemAdmins.length === 0 ? (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No system admins available.
              </Typography>
            ) : (
              allSystemAdmins.map((admin) => {
                const checked = csAssignSelectedAdminIds.includes(admin.id);
                const toggle = () => {
                  setCsAssignSelectedAdminIds(checked
                    ? csAssignSelectedAdminIds.filter((id) => id !== admin.id)
                    : [...csAssignSelectedAdminIds, admin.id]);
                };
                return (
                  <Box
                    key={admin.id}
                    display="flex"
                    alignItems="center"
                    sx={{
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: checked ? 'action.selected' : 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={toggle}
                  >
                    <Checkbox checked={checked} onChange={toggle} />
                    <Typography variant="body2">
                      {admin.firstName} {admin.lastName} ({admin.email})
                      {admin.isSuperAdmin && (
                        <Chip label="Super Admin" size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setCsAssignAdminsDialogOpen(false); setCsAssignAdminsRow(null); }}
          >Cancel</Button>
          <Button
            onClick={handleSaveCurriculumSubjectAdmins}
            variant="contained"
            disabled={csAssignSaving}
          >
            {csAssignSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Curriculum Subject Delete Confirmation */}
      <Dialog open={subjectDeleteDialogOpen} onClose={() => setSubjectDeleteDialogOpen(false)}>
        <DialogTitle>Delete Curriculum Subject</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{menuCurriculumSubject?.name}&quot;? This will fail if it has areas. Remove areas first from the curriculum structure view.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubjectDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCurriculumSubjectConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCurriculum ? 'Edit Curriculum' : 'Add New Curriculum'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select country, board, and curriculum subject, then provide version details. Board-specific fields appear based on the selected board type.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Country</InputLabel>
              <Select
                value={formCountryId}
                label="Country"
                onChange={(e) => {
                  const selectedCountryId = Number(e.target.value);
                  setFormCountryId(selectedCountryId);
                  setFormCurrBoardId('');
                  setFormCurrSubjectId('');
                  setFilteredCurrSubjects([]);
                  setFormScottishLevel('');
                  loadBoardsForCountry(selectedCountryId);
                }}
                disabled={!!selectedCurriculum}
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required disabled={!formCountryId || !!selectedCurriculum}>
              <InputLabel>Board</InputLabel>
              <Select
                value={formCurrBoardId}
                label="Board"
                onChange={(e) => {
                  const bid = Number(e.target.value);
                  setFormCurrBoardId(bid);
                  setFormCurrSubjectId('');
                  setFormScottishLevel('');
                  loadCurrSubjectsForBoard(bid);
                }}
              >
                {boards.map((board) => (
                  <MenuItem key={board.id} value={board.id}>
                    {board.name} ({board.boardType || 'Unknown'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required disabled={!formCurrBoardId || !!selectedCurriculum}>
              <InputLabel>Curriculum Subject</InputLabel>
              <Select
                value={formCurrSubjectId}
                label="Curriculum Subject"
                onChange={(e) => setFormCurrSubjectId(Number(e.target.value))}
              >
                {filteredCurrSubjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name} ({subject.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Version"
              value={formVersion}
              onChange={(e) => setFormVersion(e.target.value)}
              fullWidth
              placeholder="e.g., 2024-2025"
            />
            <TextField
              label="Description"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            {formCurrBoardId && (() => {
              const selectedBoard = allBoards.find(b => b.id === formCurrBoardId);
              const boardType = selectedBoard?.boardType?.toUpperCase();
              if (boardType === 'SCOTTISH') {
                return (
                  <FormControl fullWidth required>
                    <InputLabel>CfE Level</InputLabel>
                    <Select
                      value={formScottishLevel}
                      onChange={(e) => setFormScottishLevel(e.target.value)}
                      label="CfE Level"
                    >
                      {scottishLevels.map((level) => (
                        <MenuItem key={level} value={level}>
                          {formatLevel(level)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }
              if (boardType) {
                return (
                  <Alert severity="info">
                    Only Scottish boards are supported at this time. Support for {boardType} is coming soon.
                  </Alert>
                );
              }
              return null;
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formCountryId || !formCurrBoardId || !formCurrSubjectId || (allBoards.find(b => b.id === formCurrBoardId)?.boardType?.toUpperCase() === 'SCOTTISH' && !formScottishLevel)}
          >
            {selectedCurriculum ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Curriculum</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this curriculum? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CurriculumManagement;

